import { evaluate } from 'mathjs'

import { makeEquationFromPattern } from './utils/pemdas.utils'

const MIN = 1
const MAX = 9
const SQRT = `sqrt([9,4,2+2,7+2,2+7,5+4,5-1,10-1,10-1,10-1,4+5,9+7,7+9,8+8,8+1,1+8,6+3,3+6,3*3,4*4,5*5,6*6,7*7,8*8,9*9,10*10,10*10,10*10,6+8+2,3+6+7,2+4+3,1+4+4,9+6+1,10+8+7,8+8+9,1+2+1,7*2+2,4*3+4,9*2-2,3*4-3])`
const DIVISION = `[4/2,8/2,8/4,9/3,10/5,10/5,10/5,6/3,6/2]`
const PARENTHESIS_PATTERNS = [
  `[${MIN}-3]*[4-7]-[${MIN}-3]`,
  `[${MIN}-3]*[4-6]+[${MIN}-${MAX}]`,
  `[${MIN}-${MAX}]+[${MIN}-5]*[${MIN}-3]`,
  `[${MIN}-${MAX}]+${DIVISION}`,
]

const power = (eqn?: string): string => {
  if (!eqn) {
    const patterns = [`[2-4]^3`, `[2-${MAX}]^2`]
    return patterns[Math.floor(Math.random() * patterns.length)]
  }
  eqn = makeEquationFromPattern(eqn)
  const value = evaluate(eqn)
  if (value < 5) {
    return `${eqn}^3`
  } else {
    return `${eqn}^2`
  }
}

const parenthesis = (): string => `(${PARENTHESIS_PATTERNS[Math.floor(Math.random() * PARENTHESIS_PATTERNS.length)]})`

const pemdasPatterns: { [key: string]: { patterns: string[]; resetTaskHistory: number } } = {
  '1': {
    patterns: [
      `([${MIN}-${MAX}]+[${MIN}-${MAX}])*[${MIN}-${MAX}][+,-][${MIN}-${MAX}]*[${MIN}-${MAX}]`,
      `([5-${MAX}]-[${MIN}-4])*[${MIN}-${MAX}][+,-][${MIN}-${MAX}]`,
      `[${MIN}-${MAX}]*([${MIN}-${MAX}]+[${MIN}-${MAX}])[+,-][${MIN}-${MAX}]`,
      `[${MIN}-${MAX}]*([5-${MAX}]-[${MIN}-${MAX}])[+,-]${DIVISION}`,
      `${power()}*[${MIN}-${MAX}][+,-][${MIN}-${MAX}]`,
      `[${MIN}-${MAX}]*${power()}[+,-][${MIN}-${MAX}]`,
      `${SQRT}*[${MIN}-${MAX}][+,-][${MIN}-${MAX}]`,
      `[${MIN}-${MAX}]*${SQRT}[+,-][${MIN}-${MAX}]`,
      `${power()}[+,-][${MIN}-${MAX}]`,
      `[${MIN}-${MAX}][+,-]${SQRT}`,
    ],
    resetTaskHistory: 5,
  },
  '2': {
    patterns: [
      `([${MIN}-${MAX}]+[${MIN}-${MAX}])*[4-${MAX}]-[${MIN}-3]+[${MIN}-${MAX}]`, // "(7+7)*5-2+8"
      `[${MIN}-${MAX}]+[${MIN}-${MAX}]*([${MIN}-${MAX}]+[${MIN}-${MAX}])-[${MIN}-3]`, // "2+9*(10+4)-2"
      `([${MIN}-${MAX}]+[${MIN}-${MAX}])*${DIVISION}-[${MIN}-3]+[${MIN}-${MAX}]`, // "(8+1)*8/2-2+9"
      `[${MIN}-${MAX}]+([${MIN}-${MAX}]+[${MIN}-${MAX}])*${DIVISION}-[${MIN}-3]`, // "3+(10+9)*6/2-1"
      `([6-${MAX}][+,-][${MIN}-4])*[4-${MAX}][+,-][${MIN}-3][+,-][${MIN}-${MAX}]`, // "(10-4)*5-2+1"
      `[${MIN}-${MAX}][+,-][${MIN}-${MAX}]*([6-${MAX}][+,-][${MIN}-4])[+,-][${MIN}-3]`, // "10-10*(6-4)+3"
      `${DIVISION}[+,-]([6-${MAX}][+,-][${MIN}-4])*[${MIN}-${MAX}][+,-][${MIN}-${MAX}]`, // "8/4+(6+4)*5+2"
      `${power()}[+,-][${MIN}-${MAX}]*${DIVISION}[+,-][${MIN}-${MAX}]`, // "3^3-6*4/2-3"
      `${power()}*[6-${MAX}]-${DIVISION}[+,-][${MIN}-${MAX}]`, // "3^3*7-10/5-6"
      `[6-${MAX}]*${power()}[+,-]${DIVISION}[+,-][${MIN}-${MAX}]`, // "7*7^3-10/5+6"
      `[${MIN}-${MAX}]+[${MIN}-${MAX}]*${power()}[+,-][${MIN}-${MAX}]`, // "8+9*4^3+6"
      `${SQRT}+[${MIN}-${MAX}]*${DIVISION}[+,-][${MIN}-${MAX}]`, // "sqrt(8+8)+3*10/5-2"
      `[${MIN}-${MAX}][+,-][6-${MAX}]*${SQRT}[+,-]${DIVISION}`, // "7+6*sqrt(7+9)-9/3"
      `[${MIN}-${MAX}][+,-][${MIN}-${MAX}]+[${MIN}-${MAX}]*${SQRT}`, // "6+1+10*sqrt(3+6)"
    ],
    resetTaskHistory: 8,
  },
  '3': {
    patterns: [
      `[${MIN}-${MAX}]*${parenthesis()}+[${MIN}-${MAX}]*[${MIN}-${MAX}]-${DIVISION}`,
      `${parenthesis()}*[${MIN}-${MAX}]+[${MIN}-${MAX}]*[${MIN}-${MAX}][+,-][${MIN}-3]`,
      `[${MIN}-${MAX}]*[${MIN}-${MAX}]+[${MIN}-${MAX}]*${parenthesis()}[+,-][${MIN}-3]`,
      `${power(parenthesis())}+[${MIN}-${MAX}]*[${MIN}-${MAX}]-${parenthesis()}*[${MIN}-${MAX}]`,
      `${power()}+[${MIN}-${MAX}]*${DIVISION}+[${MIN}-${MAX}]*${parenthesis()}`,
      `[${MIN}-${MAX}]+${power()}*[${MIN}-${MAX}]+[${MIN}-${MAX}]*${parenthesis()}`,
      `[${MIN}-${MAX}]+${power(parenthesis())}+${power()}`,
      `${SQRT}+[${MIN}-${MAX}]*${DIVISION}+${parenthesis()}*${DIVISION}`,
      `${SQRT}+[${MIN}-${MAX}]*${DIVISION}+${power(parenthesis())}`,
      `[${MIN}-${MAX}]+${SQRT}+${parenthesis()}*${power()}`,
      `${power(parenthesis())}+[${MIN}-${MAX}]+${SQRT}`,
      `[${MIN}-${MAX}]+${parenthesis()}*[${MIN}-${MAX}]+${SQRT}`,
      `[${MIN}-${MAX}]+${parenthesis()}*[${MIN}-${MAX}]+[${MIN}-${MAX}]*[${MIN}-${MAX}]-${DIVISION}`,
      `[${MIN}-${MAX}]*${parenthesis()}+[${MIN}-${MAX}]*[${MIN}-${MAX}][+,-][${MIN}-3][+,-][${MIN}-3]`,
      `[${MIN}-${MAX}]*[${MIN}-${MAX}]+[${MIN}-${MAX}]*${DIVISION}[+,-][${MIN}-3]+${parenthesis()}*[${MIN}-${MAX}]`,
      `${parenthesis()}*[${MIN}-${MAX}]-[${MIN}-5]+${parenthesis()}*[${MIN}-${MAX}]+${DIVISION}`,
      `${power()}+[${MIN}-${MAX}]-${DIVISION}+${parenthesis()}[*][${MIN}-3]`,
      `[${MIN}-${MAX}]+${power()}*[${MIN}-${MAX}]+${parenthesis()}*${SQRT}`,
      `[${MIN}-${MAX}]+${parenthesis()}*${parenthesis()}[+,-]${power()}+[${MIN}-${MAX}]`,
      `${SQRT}+[${MIN}-${MAX}]*${DIVISION}+${parenthesis()}*[${MIN}-${MAX}]`,
      `${SQRT}+[${MIN}-${MAX}]+${SQRT}+${parenthesis()}*${power()}`,
      `[${MIN}-${MAX}]+${parenthesis()}*[${MIN}-${MAX}]+${SQRT}`,
    ],
    resetTaskHistory: 15,
  },
  '4': {
    patterns: [
      `[${MIN}-${MAX}]*${parenthesis()}+[${MIN}-${MAX}]*[${MIN}-${MAX}]-${DIVISION}[+,-][${MIN}-3]+${power()}`,
      `${parenthesis()}*[${MIN}-${MAX}]+[${MIN}-${MAX}]*[${MIN}-${MAX}][+,-][${MIN}-3]+${power(
        parenthesis(),
      )}[+,-][${MIN}-3]`,
      `[${MIN}-${MAX}]+[${MIN}-${MAX}]*${parenthesis()}[+,-][${MIN}-3]+${SQRT}+[${MIN}-${MAX}]`,
      `${power(parenthesis())}+[${MIN}-${MAX}]*[${MIN}-${MAX}]+[${MIN}-${MAX}]-[${MIN}-${MAX}]+${SQRT}`,
      `${power()}+[${MIN}-${MAX}]*${DIVISION}+[${MIN}-${MAX}]*${parenthesis()}-[${MIN}-${MAX}]-[${MIN}-${MAX}]`,
      `[${MIN}-${MAX}]+${power()}*[${MIN}-${MAX}]+[${MIN}-${MAX}]*${parenthesis()}-[${MIN}-${MAX}]+[${MIN}-${MAX}]`,
      `[${MIN}-${MAX}]+${power(parenthesis())}+${SQRT}[+,-][${MIN}-3]-${DIVISION}`,
      `${SQRT}+[${MIN}-${MAX}]+${parenthesis()}*${DIVISION}+[${MIN}-${MAX}]-[${MIN}-${MAX}]`,
      `${SQRT}+[${MIN}-${MAX}]+${power(parenthesis())}-[${MIN}-${MAX}]+[${MIN}-${MAX}]`,
      `[${MIN}-${MAX}]+${SQRT}+${parenthesis()}*[${MIN}-${MAX}]+${power()}[+,-][${MIN}-3]`,
      `${power(parenthesis())}+[${MIN}-${MAX}]+${SQRT}+[${MIN}-${MAX}]+${DIVISION}`,
      `[${MIN}-${MAX}]+${parenthesis()}*[${MIN}-${MAX}]+[${MIN}-${MAX}]*[${MIN}-${MAX}]-${DIVISION}[+,-][${MIN}-3]+${power()}`,
      `[${MIN}-${MAX}]*${parenthesis()}+[${MIN}-${MAX}]*[${MIN}-${MAX}][+,-][${MIN}-3][+,-][${MIN}-3][+,-][${MIN}-3]`,
      `[${MIN}-${MAX}]+[${MIN}-${MAX}]+${parenthesis()}*${DIVISION}[+,-][${MIN}-3]+[${MIN}-${MAX}]`,
      `[${MIN}-${MAX}]-[${MIN}-5]+${parenthesis()}*[${MIN}-5]+${DIVISION}-${SQRT}`,
      `${power()}+[${MIN}-${MAX}]-[${MIN}-3]+${parenthesis()}[*][${MIN}-3]+${SQRT}`,
      `[${MIN}-${MAX}]+${parenthesis()}*[${MIN}-${MAX}][+,-]${power()}+[${MIN}-${MAX}]+${power()}`,
      `${SQRT}+[${MIN}-${MAX}]+${parenthesis()}*[${MIN}-${MAX}]-${SQRT}+[${MIN}-${MAX}]`,
      `${SQRT}+[${MIN}-${MAX}]+[${MIN}-${MAX}]+${power(parenthesis())}-${SQRT}`,
    ],
    resetTaskHistory: 13,
  },
  '5': {
    patterns: [
      `[${MIN}-${MAX}]*${parenthesis()}+[${MIN}-${MAX}]+[${MIN}-${MAX}]*[${MIN}-${MAX}]-${DIVISION}[+,-][${MIN}-3]+[${MIN}-${MAX}]`,
      `[${MIN}-${MAX}]*[${MIN}-${MAX}]+[${MIN}-${MAX}]+[${MIN}-${MAX}][+,-][${MIN}-3]+${power(
        parenthesis(),
      )}[+,-][${MIN}-3]`,
      `[${MIN}-${MAX}]+[${MIN}-${MAX}]*[${MIN}-${MAX}]+[${MIN}-${MAX}]*${parenthesis()}[+,-][${MIN}-3]+${power()}+[${MIN}-${MAX}]`,
      `${power(parenthesis())}+[${MIN}-${MAX}]+[${MIN}-${MAX}]-[${MIN}-${MAX}]+${power()}[+,-][${MIN}-3]`,
      `${parenthesis()}*${DIVISION}+[${MIN}-${MAX}]+[${MIN}-${MAX}]-[${MIN}-${MAX}]-[${MIN}-${MAX}]+[${MIN}-${MAX}]`,
      `[${MIN}-${MAX}]+${power()}+[${MIN}-${MAX}]*${parenthesis()}-[${MIN}-${MAX}]+${SQRT}-${power()}`,
      `[${MIN}-${MAX}]+${power(parenthesis())}+${SQRT}[+,-][${MIN}-3]-${DIVISION}-[${MIN}-${MAX}]`,
      `[${MIN}-${MAX}]+${SQRT}+[${MIN}-${MAX}]+[${MIN}-${MAX}]+${power(parenthesis())}-[${MIN}-${MAX}]`,
      `[${MIN}-${MAX}]+${SQRT}+[${MIN}-${MAX}]*${DIVISION}+${power(parenthesis())}-${SQRT}+[${MIN}-${MAX}]`,
      `[${MIN}-${MAX}]+[${MIN}-${MAX}]+${parenthesis()}*${power()}+[${MIN}-${MAX}][+,-][${MIN}-3]-[${MIN}-${MAX}]`,
      `${DIVISION}+${power(parenthesis())}+[${MIN}-${MAX}]+${SQRT}+[${MIN}-${MAX}]+[${MIN}-${MAX}]`,
      `${power()}+[${MIN}-${MAX}]+${parenthesis()}*[${MIN}-${MAX}]+${DIVISION}-[${MIN}-${MAX}]+[${MIN}-${MAX}]`,
      `${SQRT}+${DIVISION}+[${MIN}-${MAX}]+${parenthesis()}*[${MIN}-${MAX}]+[${MIN}-${MAX}]-[${MIN}-${MAX}]`,
      `${SQRT}+[${MIN}-${MAX}]+[${MIN}-${MAX}]*${parenthesis()}[+,-][${MIN}-3][+,-][${MIN}-${MAX}][+,-][${MIN}-${MAX}]`,
      `${parenthesis()}*[${MIN}-${MAX}]+[${MIN}-${MAX}]+[${MIN}-${MAX}]+${DIVISION}[+,-]${power()}+[${MIN}-${MAX}]`,
      `${parenthesis()}*[${MIN}-${MAX}]-[${MIN}-5]+${power()}+[${MIN}-${MAX}]-[${MIN}-${MAX}]+[${MIN}-5]`,
      `[${MIN}-${MAX}]+[${MIN}-${MAX}]-${DIVISION}+${parenthesis()}[*][${MIN}-3]+${SQRT}-[${MIN}-${MAX}]`,
      `[${MIN}-${MAX}]+${parenthesis()}*[${MIN}-5][+,-]${power()}-[${MIN}-${MAX}]+[${MIN}-${MAX}]+${DIVISION}`,
      `${power()}+${DIVISION}-[${MIN}-${MAX}]+${parenthesis()}*[${MIN}-${MAX}]-[${MIN}-5]+[${MIN}-${MAX}]`,
      `${SQRT}-[${MIN}-${MAX}]+[${MIN}-${MAX}]+${power()}+${power(parenthesis())}-[${MIN}-5]`,
    ],
    resetTaskHistory: 13,
  },
}

/**
 *
 * @param difficultyLevel
 * @returns configuration object based on difficulty level
 */
export function getPatternsByDifficulty(difficultyLevel: number): { patterns: string[]; resetTaskHistory: number } {
  return pemdasPatterns[difficultyLevel] || new Error(`Invalid difficulty level: ${difficultyLevel}`)
}
